import { Col, DatePicker, Form, Input, Row, Select } from 'antd';
import { CloseOutlined, SearchOutlined } from '@ant-design/icons';
import React from 'react';
import CustomButton from 'components/custom-button';
import moment from 'moment';
import { ROLE_USER } from '../../../constants/common';

const { Option } = Select;
type FilterProps = {
  onSubmit: (value: any) => void;
};
const { RangePicker } = DatePicker;

type FilterValues = {
  username?: string;
  email?: string;
  roles?: string;
  creatorPending?: 'yes';
  tiktokUid?: string;
  tiktokLinked?: 'yes' | 'no';
  verifiedBadge?: 'PINK' | 'BLUE' | 'NONE';
  datePicker?: moment.Moment[] | null;
};

// Select phải để undefined (không phải ''): antd coi '' là một giá trị đã chọn,
// vẽ ra ô chọn rỗng và che mất placeholder.
const initialValues: FilterValues = {
  username: '',
  email: '',
  roles: undefined,
  creatorPending: undefined,
  tiktokUid: '',
  tiktokLinked: undefined,
  verifiedBadge: undefined,
  datePicker: [],
};

// Giá trị gửi lên server vẫn là mã vai trò (USER, MOD...); chỉ đổi chữ hiển thị.
const ROLE_OPTIONS = [
  { value: ROLE_USER.USER, label: 'Người dùng (USER)' },
  { value: ROLE_USER.CREATOR, label: 'Online Creator (CREATOR)' },
  { value: ROLE_USER.MOD, label: 'Quản lý (MOD)' },
  { value: ROLE_USER.AGENCY, label: 'Đối tác (AGENCY)' },
  { value: ROLE_USER.ADMIN, label: 'Admin (ADMIN)' },
  { value: ROLE_USER.STUDIO_CREATOR, label: 'Studio Creator (STUDIO_CREATOR)' },
];
const USER_MANAGER_FILTER_SELECT_CLASS = 'user-manager-filter-select';
const USER_MANAGER_FILTER_INPUT_CLASS = 'user-manager-filter-input';

const Filter: React.FC<any> = (props) => {
  const [form] = Form.useForm();
  const { onSubmit } = props;

  const onClearFilter = () => {
    // Về đúng initialValues (Select về undefined để hiện lại placeholder).
    form.resetFields();
    onSubmit({
      username: '',
      email: '',
      roles: '',
      creatorPending: '',
      tiktokUid: '',
      tiktokLinked: '',
      verifiedBadge: '',
      startDate: '',
      endDate: '',
    });
  };

  const onFormSubmit = (value: FilterValues) => {
    const { datePicker, ...rest } = value;
    let startDate = '';
    let endDate = '';

    if (datePicker) {
      const [start, end] = datePicker as moment.Moment[];

      startDate = start?.format('YYYY-MM-DD');
      endDate = end?.format('YYYY-MM-DD');
    }

    onSubmit({ ...rest, startDate, endDate });
  };

  return (
    <Form
      form={form}
      name="register"
      layout="vertical"
      onFinish={onFormSubmit}
      initialValues={initialValues}
      className="custom-form user-manager-filter-form"
    >
      <Row gutter={[16, 8]}>
        <Col xl={6} lg={8} md={12} xs={24}>
          <Form.Item name="username" label="TikTok ID">
            <Input
              placeholder="Nhập TikTok ID"
              allowClear
              size="small"
              className={USER_MANAGER_FILTER_INPUT_CLASS}
            />
          </Form.Item>
        </Col>
        <Col xl={6} lg={8} md={12} xs={24}>
          <Form.Item name="roles" label="Vai trò">
            <Select
              placeholder="Tất cả vai trò"
              size="small"
              allowClear
              options={ROLE_OPTIONS}
              // Danh sách được rộng hơn ô (tối thiểu bằng ô) để "Studio Creator (STUDIO_CREATOR)" không bị cắt.
              popupMatchSelectWidth={false}
              className={USER_MANAGER_FILTER_SELECT_CLASS}
            />
          </Form.Item>
        </Col>
        <Col xl={6} lg={8} md={12} xs={24}>
          <Form.Item name="creatorPending" label="Yêu cầu Creator">
            <Select placeholder="Tất cả" size="small" allowClear className={USER_MANAGER_FILTER_SELECT_CLASS}>
              <Option value="yes">Chờ Admin xác nhận</Option>
            </Select>
          </Form.Item>
        </Col>
        <Col xl={6} lg={8} md={12} xs={24}>
          <Form.Item name="email" label="Email">
            <Input
              placeholder="Nhập email"
              allowClear
              size="small"
              className={USER_MANAGER_FILTER_INPUT_CLASS}
            />
          </Form.Item>
        </Col>
        <Col xl={6} lg={8} md={12} xs={24}>
          <Form.Item name="tiktokUid" label="UID TikTok">
            <Input
              placeholder="Dán UID (cột B file TikTok)"
              allowClear
              size="small"
              inputMode="numeric"
              className={USER_MANAGER_FILTER_INPUT_CLASS}
            />
          </Form.Item>
        </Col>
        <Col xl={6} lg={8} md={12} xs={24}>
          <Form.Item name="tiktokLinked" label="Liên kết TikTok">
            <Select
              placeholder="Tất cả"
              size="small"
              allowClear
              className={USER_MANAGER_FILTER_SELECT_CLASS}
            >
              <Option value="yes">Đã gắn UID</Option>
              <Option value="no">Chưa gắn UID</Option>
            </Select>
          </Form.Item>
        </Col>
        <Col xl={6} lg={8} md={12} xs={24}>
          <Form.Item label="Tìm kiếm theo ngày" name="datePicker">
            <RangePicker
              size="small"
              style={{ width: '100%' }}
              allowClear
              // Nhãn ngắn: ô xl=6 ở màn 1280 chỉ ~228px, "Ngày bắt đầu"/"Ngày kết thúc" bị cắt "…".
              placeholder={['Từ ngày', 'Đến ngày']}
            />
          </Form.Item>
        </Col>
        <Col xl={6} lg={8} md={12} xs={24}>
          <Form.Item name="verifiedBadge" label="Xác minh danh tính">
            <Select placeholder="Tất cả" size="small" allowClear className={USER_MANAGER_FILTER_SELECT_CLASS}>
              <Option value="PINK">Tích Hồng</Option>
              <Option value="BLUE">Tích Xanh</Option>
              <Option value="NONE">Không</Option>
            </Select>
          </Form.Item>
        </Col>
      </Row>

      <Row>
        <Col span={24}>
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <CustomButton
              bgColor="secondary-green"
              htmlType="submit"
              text="Tìm kiếm"
              icon={<SearchOutlined />}
            />
            <CustomButton
              text="Xoá lọc"
              bgColor="primary-grey"
              icon={<CloseOutlined />}
              className="ml-5"
              onClick={onClearFilter}
            />
          </div>
        </Col>
      </Row>
    </Form>
  );
};

export default Filter;
